import { useCallback, useEffect, useState } from 'react'
import { Bloc, Bouton, Icone, Modale, Puce, SquelettePage } from '../ui'

import { useLangue, useTextes } from '../LangueContexte'
import { Bandeau, Champ } from './Champs'
import { interpoler } from '../i18n'
import {
  annulerSuppression,
  demanderExport,
  etatConfidentialite,
  programmerSuppression,
  retirerConsentement,
  telechargerExport,
  type EtatConfidentialite,
  type NatureConsentement,
} from '../apiConfidentialite'

/**
 * « Mes données » — l'écran par lequel s'exercent les droits de la loi 09-08. Revu le 13/09/2026
 * (maquette `docs/ui/pwa/confidentialite.html`).
 *
 * **Trois gestes, et deux d'entre eux sont irréversibles à terme.** L'écran les traite comme tels :
 * la suppression demande une confirmation explicite — un mot à saisir, dans une feuille —, annonce
 * son délai de trente jours, et **dit ce qui survivra**. Une suppression qui laisse des avis en
 * ligne sans l'avoir annoncé est une mauvaise surprise ; annoncée, c'est une règle que l'on
 * comprend.
 *
 * **Ce que cet écran ne fait pas.** Il n'offre pas de retirer les conditions d'utilisation ni la
 * politique de confidentialité : leur retrait est la suppression du compte, et une case à cocher
 * qui ne ferait rien serait pire que pas de case du tout. La pastille le dit à la place du bouton.
 * Il ne demande pas non plus le mot de passe : l'API ne l'attend pas, et un champ qui ne serait
 * pas vérifié donnerait une impression de sécurité sans en apporter.
 */
export function Confidentialite() {
  const t = useTextes()
  const langue = useLangue()

  const [etat, setEtat] = useState<EtatConfidentialite | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [feuille, setFeuille] = useState(false)
  const [saisie, setSaisie] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)

  const charger = useCallback(async () => {
    try {
      setEtat(await etatConfidentialite())
    } catch {
      setErreur(t.chargement)
    }
  }, [t.chargement])

  useEffect(() => {
    void charger()
  }, [charger])

  const agir = async (action: () => Promise<unknown>) => {
    setOccupe(true)
    setErreur(null)

    try {
      await action()
      await charger()
    } catch {
      setErreur(t.chargement)
    } finally {
      setOccupe(false)
    }
  }

  if (etat === null) {
    return <SquelettePage />
  }

  const dateLongue = (valeur: string) =>
    new Intl.DateTimeFormat(langue, { dateStyle: 'long' }).format(new Date(valeur))

  const libelles: Record<NatureConsentement, string> = {
    TermsOfService: t.consentementTermsOfService,
    PrivacyPolicy: t.consentementPrivacyPolicy,
    MarketingEmails: t.consentementMarketingEmails,
  }

  const motCle = t.confidentialiteMotCle
  const pretASupprimer = saisie.trim().toLocaleUpperCase() === motCle.toLocaleUpperCase()
  const archive = etat.availableExport

  return (
    <section className="confidentialite">
      <div className="page-entete">
        <h2>{t.confidentialiteTitre}</h2>
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}

      <Bloc titre={t.confidentialiteConsentements}>
        <ul className="liste-lignes">
          {etat.consents.map((consentement) => (
            <li key={consentement.type}>
              <span className="liste-lignes__corps">
                <span className="liste-lignes__titre">{libelles[consentement.type]}</span>
                <span className="liste-lignes__meta">
                  {t.confidentialiteVersion} {consentement.policyVersion} · {t.confidentialiteAcceptee}{' '}
                  {dateLongue(consentement.grantedAt)}
                  {consentement.revokedAt !== null &&
                    ` · ${interpoler(t.confidentialiteRetireLe, { date: dateLongue(consentement.revokedAt) })}`}
                </span>
              </span>

              {consentement.revokedAt !== null ? (
                <Puce>{t.confidentialiteRetire}</Puce>
              ) : consentement.isRevocable ? (
                <Bouton
                  apparence="discret"
                  taille="sm"
                  charge={occupe}
                  onClick={() => void agir(() => retirerConsentement(consentement.type))}
                >
                  {t.confidentialiteRetirer}
                </Bouton>
              ) : (
                <Puce>{t.confidentialiteNonRevocableSeule}</Puce>
              )}
            </li>
          ))}
        </ul>
        <p className="tertiaire confidentialite__note">{t.confidentialiteNonRevocable}</p>
      </Bloc>

      <Bloc titre={t.confidentialiteExport}>
        <div className="pile">
          <p className="secondaire">{t.confidentialiteExportAide}</p>

          {archive !== null && (
            <div className="bloc bloc--sourd confidentialite__archive">
              <div className="confidentialite__archive-corps">
                <p className="fort">
                  {interpoler(t.confidentialiteArchiveDu, {
                    date: dateLongue(archive.completedAt ?? archive.requestedAt),
                  })}
                </p>
                {archive.resultExpiresAt !== null && (
                  <p className="secondaire">
                    {t.confidentialiteExpireLe} {dateLongue(archive.resultExpiresAt)}
                  </p>
                )}
              </div>
              <Bouton
                apparence="secondaire"
                taille="sm"
                charge={occupe}
                onClick={() => void agir(() => telechargerExport(archive.id))}
              >
                <Icone nom="telechargement" taille="sm" /> {t.confidentialiteTelecharger}
              </Bouton>
            </div>
          )}

          {etat.pendingExport !== null ? (
            <div className="bloc bloc--sourd confidentialite__archive">
              <Icone nom="horloge" />
              <p className="secondaire confidentialite__archive-corps">{t.confidentialiteExportEnCours}</p>
            </div>
          ) : (
            <div>
              <Bouton apparence="secondaire" charge={occupe} onClick={() => void agir(demanderExport)}>
                <Icone nom="export" taille="sm" />{' '}
                {archive !== null ? t.confidentialiteDemanderNouvelle : t.confidentialiteDemanderExport}
              </Bouton>
            </div>
          )}
        </div>
      </Bloc>

      <Bloc titre={t.confidentialiteSuppression} variante="danger">
        {etat.deletionScheduledFor !== null ? (
          <div className="pile">
            <Bandeau ton="info" icone={<Icone nom="horloge" />}>
              {t.confidentialiteSuppressionPrevue} {dateLongue(etat.deletionScheduledFor)}
            </Bandeau>
            <div>
              <Bouton apparence="secondaire" charge={occupe} onClick={() => void agir(annulerSuppression)}>
                {t.confidentialiteAnnulerSuppression}
              </Bouton>
            </div>
          </div>
        ) : (
          <div className="pile">
            <p className="secondaire">{t.confidentialiteSuppressionAide}</p>
            <Bandeau ton="attention" icone={<Icone nom="info" />}>
              {t.confidentialiteCeQuiSurvit}
            </Bandeau>
            <div>
              <Bouton
                apparence="danger"
                onClick={() => {
                  setSaisie('')
                  setFeuille(true)
                }}
              >
                {t.confidentialiteSupprimerCompte}
              </Bouton>
            </div>
          </div>
        )}
      </Bloc>

      <p className="tertiaire">{t.confidentialiteHorloge}</p>

      {/*
        Deux temps, et c'est délibéré. Un bouton unique sur un geste dont la seconde moitié est
        irréversible transforme un clic malheureux en perte de compte — et le délai de trente
        jours ne protège que si la personne se rend compte de ce qu'elle a fait. Le mot à saisir
        est celui de la langue de l'écran.
      */}
      {feuille && (
        <Modale
          titre={t.confidentialiteConfirmerTitre}
          feuille
          onFermer={() => setFeuille(false)}
          pied={
            <>
              <Bouton apparence="discret" onClick={() => setFeuille(false)}>
                {t.annuler}
              </Bouton>
              <Bouton
                apparence="danger"
                desactive={!pretASupprimer}
                charge={occupe}
                onClick={() =>
                  void agir(async () => {
                    await programmerSuppression()
                    setFeuille(false)
                  })
                }
              >
                {t.confidentialiteSupprimerDefinitivement}
              </Bouton>
            </>
          }
        >
          <div className="pile">
            <Bandeau ton="erreur" icone={<Icone nom="alerte" />}>
              {t.confidentialiteBailActif}
            </Bandeau>
            <Champ
              id="confirmation-suppression"
              libelle={interpoler(t.confidentialiteSaisirMot, { mot: motCle })}
              valeur={saisie}
              autoComplete="off"
              onChange={setSaisie}
            />
          </div>
        </Modale>
      )}
    </section>
  )
}
